import axios, { CancelTokenSource } from "axios";
import React, { useState, useRef, useEffect } from "react";
import {
    Modal,
    Button,
    message,
    Typography,
    Form,
    Select,
    Input,
    Col,
    Row,
    Divider,
    Collapse,
} from "antd";
import { SettingOutlined } from "@ant-design/icons";
import AutomationProgressComponent, {
    AutomationProgressComponentRef,
} from "./AutomationProgressComponent";
import readUserInputsPrompt from "./prompts/readUserInputs";
import questionVarsPrompt from "./prompts/questionVars";
import derivedVarsInsightsPrompt from "./prompts/derivedVarsInsights";
import { updateBlockForEvaluationAndFeedback } from "./automation-utils";
import websocketService from "src/services/websocket";

// Import the data.json file
import dataJson from "./automation-utils/data.json";

const { Text } = Typography;
const { TextArea } = Input;
const FormItem = Form.Item;
const { Panel } = Collapse;

interface AutomateEvaluationAndFeedbackSetupProps {
    worksheet?: any;
    block_id?: string;
    level_id?: string;
    block?: any;
    setBlock?: any;
}

// Define the progress steps for automation - updated to match CompletedSteps interface
interface AutomationSteps {
    "collecting-context": {
        "variables-data-structure": boolean;
        "read-user-inputs": boolean;
        "derived-variables": boolean;
    };
    "generating-functions": {
        processing: boolean;
    };
}

function getComponents(block: any) {
    const components = block?.data?.all_in_one?.components;
    // filter for generic_table and condition

    // for generic_table check if skip_evaluation and disable_interaction are false.

    const filteredComponents = components.filter((i: any) => {
        if (i.type === "generic_table") {
            return !i.skip_evaluation && !i.disable_interaction;
        }
        if (i.type === "condition") {
            return !i.skip_evaluation && !i.disable_interaction;
        }
        return false;
    });

    // return filteredComponents.type capitalized fully
    return filteredComponents.map((i: any) => {
        return i.type.toUpperCase();
    });
}

const AutomateEvaluationAndFeedbackSetup: React.FC<
    AutomateEvaluationAndFeedbackSetupProps
> = (props) => {
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [responseData, setResponseData] = useState<any>(null);
    const [model, setModel] = useState("gemini-2.0-flash");
    const [prompt, setPrompt] = useState(
        "Generate automated evaluation and feedback for this worksheet.",
    );
    const [context, setContext] = useState<any>(null);
    const [form] = Form.useForm();
    const inputRef = React.useRef<any>();

    // WebSocket state for evaluationAndFeedbackAutomation
    const [wsConnected, setWsConnected] = useState(false);
    const [wsClientId, setWsClientId] = useState<string | null>(null);

    // Progress tracking state
    const [automationProgress, setAutomationProgress] =
        useState<AutomationSteps>({
            "collecting-context": {
                "variables-data-structure": false,
                "read-user-inputs": false,
                "derived-variables": false,
            },
            "generating-functions": {
                processing: false,
            },
        });

    const progressRef = useRef<AutomationProgressComponentRef>(null);

    const { block, setBlock } = props;

    // console.log("block-----", block);

    useEffect(() => {
        if (!visible) return;
        inputRef.current?.focus();
        if (!context) onOpen();
    }, [visible]);

    // WebSocket connection setup for evaluationAndFeedbackAutomation
    useEffect(() => {
        if (visible && !wsConnected) {
            setupWebSocket();
        }

        return () => {
            if (wsConnected) {
                websocketService.disconnect();
            }
        };
    }, [visible, wsConnected]);

    const setupWebSocket = async () => {
        try {
            websocketService.setCallbacks({
                onConnect: () => {
                    setWsConnected(true);
                    console.log("WebSocket connected for automation");
                },
                onDisconnect: () => {
                    setWsConnected(false);
                    setWsClientId(null);
                    console.log("WebSocket disconnected");
                },
                onError: (error) => {
                    console.error("WebSocket error:", error);
                    message.error("WebSocket connection error");
                    setWsConnected(false);
                },
                onSuccess: (result) => {
                    console.log("Automation completed successfully:", result);
                    console.log("result.result", result.result);
                    console.log(
                        "Full result structure:",
                        JSON.stringify(result, null, 2),
                    );

                    try {
                        const formattedResponse =
                            updateBlockForEvaluationAndFeedback(
                                block,
                                result.result,
                            );
                        setResponseData(formattedResponse);
                        updateStepProgress(
                            "generating-functions",
                            "processing",
                            true,
                        );
                        message.success("Automation completed successfully!");
                    } catch (error) {
                        console.error("Error processing result:", error);
                        console.error("Result structure:", result);
                        message.error("Failed to process automation result");
                    }
                    setLoading(false);
                },
                onMessage: (message) => {
                    console.log("WebSocket message received:", message);
                },
            });

            await websocketService.connect();
            setWsClientId(websocketService.getClientId());
        } catch (error) {
            console.error("Failed to setup WebSocket:", error);
            message.error("Failed to connect to WebSocket server");
            setWsConnected(false);
        }
    };

    // Progress update handlers
    const handleProgressChange = (newProgress: AutomationSteps) => {
        setAutomationProgress(newProgress);
        console.log("Automation progress updated:", newProgress);
    };

    const updateStepProgress = (
        stepId: keyof AutomationSteps,
        substepId: string,
        completed: boolean,
    ) => {
        setAutomationProgress((prev) => ({
            ...prev,
            [stepId]: {
                ...prev[stepId],
                [substepId]: completed,
            },
        }));
    };

    const resetProgress = () => {
        const resetProgress: AutomationSteps = {
            "collecting-context": {
                "variables-data-structure": false,
                "read-user-inputs": false,
                "derived-variables": false,
            },
            "generating-functions": {
                processing: false,
            },
        };
        setAutomationProgress(resetProgress);
    };

    const onOpen = async () => {
        setLoading(true);

        const options = {
            method: "POST",
            url: `${process.env.REACT_APP_HOMEWORK_SERVER_QA_API_ENDPOINT}/v3/personalizedLearning/getLLMContext`,
            data: {
                data: {
                    worksheet_id: props.worksheet?.id,
                    block_id: props.block_id,
                    level_id: props.level_id,
                },
            },
        };

        return axios
            .request(options)
            .then(function (response) {
                setLoading(false);
                if (!response.data?.success) {
                    message.error("Failed to get context");
                    setContext("Failed");
                    return;
                }
                setContext(response.data?.data);
                return response.data?.data;
            })
            .catch(function (error) {
                setLoading(false);
                if (axios.isCancel(error)) {
                    message.error("Request was cancelled due to timeout");
                } else {
                    message.error("Failed to get context");
                }
            });
    };

    const getVariablesContext = async () => {
        const options = {
            method: "POST",
            url: `${process.env.REACT_APP_HOMEWORK_SERVER_QA_API_ENDPOINT}/v3/personalizedLearning/quesBlockSummary`,
            data: {
                data: {
                    sys_prompt: questionVarsPrompt,
                    model: model,
                    worksheet_title: props.worksheet?.title,
                    context: context,
                },
            },
            timeout: 1000000,
        };

        return axios
            .request(options)
            .then(function (response) {
                if (!response.data?.success) {
                    message.error("Failed to get variables context");
                    updateStepProgress(
                        "collecting-context",
                        "variables-data-structure",
                        false,
                    );
                    return null;
                }
                return response.data?.data;
            })
            .catch(function (error) {
                if (axios.isCancel(error)) {
                    message.error("Request was cancelled due to timeout");
                } else {
                    message.error("Failed to get variables context");
                }
                updateStepProgress(
                    "collecting-context",
                    "variables-data-structure",
                    false,
                );
                return null;
            });
    };

    const getReadUserInputsContext = async () => {
        const options = {
            method: "POST",
            url: `${process.env.REACT_APP_HOMEWORK_SERVER_QA_API_ENDPOINT}/v3/personalizedLearning/quesBlockSummary`,
            data: {
                data: {
                    sys_prompt: readUserInputsPrompt,
                    model: model,
                    worksheet_title: props.worksheet?.title,
                    context: context,
                },
            },
            timeout: 1000000,
        };

        return axios
            .request(options)
            .then(function (response) {
                if (!response.data?.success) {
                    message.error("Failed to get variables context");
                    updateStepProgress(
                        "collecting-context",
                        "read-user-inputs",
                        false,
                    );
                    return null;
                }
                return response.data?.data;
            })
            .catch(function (error) {
                if (axios.isCancel(error)) {
                    message.error("Request was cancelled due to timeout");
                } else {
                    message.error("Failed to get variables context");
                }
                updateStepProgress(
                    "collecting-context",
                    "read-user-inputs",
                    false,
                );
                return null;
            });
    };

    const getDerivedVarsInsightsContext = async (params: any) => {
        const modifiedPrompt = `
        ${derivedVarsInsightsPrompt}
        Question Text: ${params.question_text}
        Question Audio: ${JSON.stringify(params.question_audio)}
        Variables Context: ${params.variables_context}
        Read User Inputs Context: ${params.read_user_inputs_context}
        `;
        const options = {
            method: "POST",
            url: `${process.env.REACT_APP_HOMEWORK_SERVER_QA_API_ENDPOINT}/v3/personalizedLearning/quesBlockSummary`,
            data: {
                data: {
                    sys_prompt: modifiedPrompt,
                    model: model,
                    worksheet_title: props.worksheet?.title,
                    context: context,
                },
            },
            timeout: 1000000,
        };

        return axios
            .request(options)
            .then(function (response) {
                if (!response.data?.success) {
                    message.error(
                        "Failed to get derived vars insights context",
                    );
                    updateStepProgress(
                        "collecting-context",
                        "derived-variables",
                        false,
                    );
                    return null;
                }
                return response.data?.data;
            })
            .catch(function (error) {
                if (axios.isCancel(error)) {
                    message.error("Request was cancelled due to timeout");
                } else {
                    message.error(
                        "Failed to get derived vars insights context",
                    );
                }
                updateStepProgress(
                    "collecting-context",
                    "derived-variables",
                    false,
                );
                return null;
            });
    };

    const onCallAPI = async () => {
        setLoading(true);

        // Reset progress at the start
        resetProgress();

        // Start the automation process

        // Second step: Get variables context
        const variablesContext = await getVariablesContext();
        updateStepProgress(
            "collecting-context",
            "variables-data-structure",
            true,
        );

        const readUserInputsContext = await getReadUserInputsContext();
        updateStepProgress("collecting-context", "read-user-inputs", true);

        const questionText = block?.data?.other?.description;
        const questionTextFormatted = questionText
            .map((i: any) => i.text)
            .join("\n");

        const derivedVarsInsightsContext = await getDerivedVarsInsightsContext({
            question_text: questionTextFormatted,
            question_audio: block?.data?.all_in_one?.question_audio,
            variables_context: variablesContext,
            read_user_inputs_context: readUserInputsContext,
        });
        updateStepProgress("collecting-context", "derived-variables", true);

        console.log("derivedVarsInsightsContext", {
            variablesContext,
            readUserInputsContext,
            derivedVarsInsightsContext,
        });

        // Process derivedVarsInsightsContext to extract function insights
        let functionInsights = [];
        if (derivedVarsInsightsContext?.summary) {
            try {
                // Extract JSON from the markdown code block
                const jsonMatch = derivedVarsInsightsContext.summary.match(
                    /```json\n([\s\S]*?)\n```/,
                );
                if (jsonMatch) {
                    functionInsights = JSON.parse(jsonMatch[1]);
                }
            } catch (error) {
                console.error("Error parsing function insights:", error);
                message.error("Failed to parse function insights");
            }
        }

        let requestData = {
            model: model,
            game_data: {
                developer_prompt: `
                Question Text: ${questionTextFormatted},
                User Request: ${prompt}
                `,
                variables_context: variablesContext.summary,
                system_context: readUserInputsContext.summary,
            },
            feedback_data: {
                sound: block?.data?.all_in_one?.question_audio,
            },
            questionVars: block?.data?.backend?.question_variables,
            metadata: {
                components: getComponents(block),
            },
            function_insights: functionInsights,
        };

        try {
            // Use WebSocket for evaluationAndFeedbackAutomation if connected
            if (wsConnected) {
                console.log(
                    "Using WebSocket for evaluationAndFeedbackAutomation",
                );
                websocketService.startAutomation(requestData);
            } else {
                // Fallback to REST API if WebSocket is not connected
                console.log(
                    "Using REST API for evaluationAndFeedbackAutomation",
                );
                const options = {
                    method: "POST",
                    url: `${process.env.REACT_APP_HOMEWORK_SERVER_QA_API_ENDPOINT}/v3/personalizedLearning/evaluationAndFeedbackAutomation`,
                    data: requestData,
                    timeout: 10000000,
                };

                const response = await axios.request(options);
                const formattedResponse = updateBlockForEvaluationAndFeedback(
                    block,
                    response.data,
                );
                setResponseData(formattedResponse);

                console.log(
                    "response--------------------",
                    response.data,
                    formattedResponse,
                );

                // Mark processing as complete
                updateStepProgress("generating-functions", "processing", true);
                message.success("Automation completed successfully!");
                setLoading(false);
            }
        } catch (error) {
            if (axios.isCancel(error)) {
                message.error("Request was cancelled due to timeout");
            } else {
                message.error("API call failed");
                console.log("error--------------------", error);
            }
            updateStepProgress("generating-functions", "processing", false);
            setLoading(false);
        }
    };

    const handleCompleteAllSteps = () => {
        progressRef.current?.completeAllSteps();
    };

    const handleResetAllSteps = () => {
        progressRef.current?.resetAllSteps();
    };

    const handleApplyDataJson = () => {
        try {
            // Use the imported data.json and current block
            const formattedResponse = updateBlockForEvaluationAndFeedback(
                block,
                dataJson.data,
            );

            // Apply the updated block
            setBlock(formattedResponse.updatedBlock);

            // Set the response data to show the generated functions
            setResponseData(formattedResponse);

            message.success("Data from data.json applied successfully!");
        } catch (error) {
            console.error("Error applying data.json:", error);
            message.error("Failed to apply data from data.json");
        }
    };

    return (
        <>
            <Button
                type="primary"
                icon={<SettingOutlined />}
                onClick={() => setVisible(true)}
            >
                Automate Evaluation & Feedback
            </Button>

            <Modal
                title="Automate Evaluation and Feedback Setup"
                open={visible}
                onCancel={() => setVisible(false)}
                onOk={() => setVisible(false)}
                width={1000}
                footer={[
                    <Button key="cancel" onClick={() => setVisible(false)}>
                        Close
                    </Button>,
                ]}
            >
                <Form>
                    <FormItem label="Model">
                        <Select
                            value={model}
                            onChange={(value) => {
                                setModel(value);
                            }}
                        >
                            {[
                                "gemini-2.5-pro",
                                "gemini-2.5-flash",
                                "gemini-2.5-flash-lite-preview-06-17",
                                "gemini-2.0-flash",
                                "gemini-2.0-flash-lite",
                                "gemini-1.5-flash",
                                "gemini-1.5-flash-8b",
                                "gemini-1.5-pro",
                            ].map((i) => (
                                <Select.Option key={i} value={i}>
                                    {i}
                                </Select.Option>
                            ))}
                        </Select>
                    </FormItem>

                    <FormItem label="Prompt">
                        <TextArea
                            ref={inputRef}
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            rows={4}
                            placeholder="Enter your prompt for automated evaluation..."
                        />
                    </FormItem>

                    <FormItem>
                        <Button
                            type="primary"
                            onClick={onCallAPI}
                            loading={loading}
                            style={{ marginBottom: 16, marginRight: 8 }}
                        >
                            Start Automation
                        </Button>
                        {/* <Button
                            type="default"
                            onClick={handleApplyDataJson}
                            style={{ marginBottom: 16 }}
                        >
                            Apply Data.json
                        </Button> */}
                        {wsConnected ? (
                            <div style={{ marginBottom: 16, color: "#52c41a" }}>
                                ✓ WebSocket Connected (Real-time progress)
                            </div>
                        ) : (
                            <div style={{ marginBottom: 16, color: "#faad14" }}>
                                ⚠ WebSocket not connected (Using REST API)
                            </div>
                        )}
                    </FormItem>
                </Form>

                {loading && (
                    <>
                        <div style={{ marginBottom: 16 }}>
                            <Text strong>Automation Progress</Text>
                            {/* currentProgress && (
                                <div style={{ marginTop: 8, color: '#666' }}>
                                    <Text type="secondary">
                                        {currentProgress.progress}% - {currentProgress.message}
                                    </Text>
                                </div>
                            ) */}
                        </div>
                        <AutomationProgressComponent
                            ref={progressRef}
                            completedSteps={automationProgress}
                            onCompletedStepsChange={handleProgressChange}
                            className="mb-4"
                        />
                    </>
                )}

                {/* API Response */}
                {responseData && (
                    <div style={{ marginTop: 16 }}>
                        <Button
                            onClick={() => {
                                setBlock(responseData?.updatedBlock);
                            }}
                        >
                            Apply Data
                        </Button>
                        <Text
                            strong
                            style={{ display: "block", marginBottom: 16 }}
                        >
                            Generated Functions
                        </Text>
                        <Collapse
                            accordion
                            style={{ maxHeight: "70vh", overflowY: "auto" }}
                        >
                            {responseData?.functionsList?.map(
                                (functionItem: any, index: number) => (
                                    <Panel
                                        header={
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 8,
                                                }}
                                            >
                                                <Text strong>
                                                    {functionItem.name}
                                                </Text>
                                                <Text
                                                    type="secondary"
                                                    style={{ fontSize: "12px" }}
                                                >
                                                    Function {index + 1}
                                                </Text>
                                            </div>
                                        }
                                        key={index}
                                    >
                                        <div style={{ padding: "8px 0" }}>
                                            <Text
                                                type="secondary"
                                                style={{
                                                    display: "block",
                                                    marginBottom: 8,
                                                }}
                                            >
                                                Output:
                                            </Text>
                                            <div
                                                style={{
                                                    backgroundColor: "#f5f5f5",
                                                    padding: 12,
                                                    borderRadius: 6,
                                                    fontFamily: "monospace",
                                                    whiteSpace: "pre-wrap",
                                                    fontSize: "13px",
                                                }}
                                            >
                                                {functionItem.output}
                                            </div>
                                        </div>
                                    </Panel>
                                ),
                            )}
                        </Collapse>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default AutomateEvaluationAndFeedbackSetup;
